import { produce } from 'immer';
import { APIUserAbortError } from 'openai';
import { use } from 'react';

import { AtlasAiHero } from './AtlasAiHero';
import { CogniteAtlasAPIClient } from './client';
import { ChatContext } from '#components/chat/contexts/ChatContext';
import { useChatTools } from '#components/chat/contexts/ChatToolContext';
import { useOnMessage } from '#components/chat/hooks/useOnMessage';
import type { ChatProviderMessage } from '#components/chat/types';
import { hasRole } from '#components/chat/utils';
import { ProjectContext } from '#contexts/ProjectContext';
import { useCogniteClient } from '#hooks/useCogniteClient';

class ToolInvocationError extends Error {}

export function AtlasAiBackend() {
  const { config } = use(ProjectContext);
  const defaultPayload = {
    model: config.feature.ai.model,
    // biome-ignore lint/style/useNamingConvention: API expects snake_case
    max_tokens: config.feature.ai.maxTokens,
    temperature: config.feature.ai.temperature,
  } as const;
  const { addMessage, systemPrompts, setStreamingMessage } = use(ChatContext);
  const cogniteClient = useCogniteClient();
  const aiClient = new CogniteAtlasAPIClient(CogniteAtlasAPIClient.getDefaultClientOptions(cogniteClient));
  const { toolsByName, toolDefinitions } = useChatTools();

  useOnMessage(async (message, messages) => {
    const contextMessages = [
      ...systemPrompts.map((content) => ({ role: 'system' as const, content })),
      ...[...messages, message].map(({ message, completionMessage }) => {
        return {
          ...message,
          content: completionMessage ?? message.content ?? '',
        };
      }),
    ].filter((it, _index, arr) => {
      // Filter out tool call requests without valid responses to avoid missing tool call response API errors
      if (!('tool_calls' in it)) {
        return true;
      }
      return it.tool_calls?.every(({ id }) => arr.find((m) => 'tool_call_id' in m && m.tool_call_id === id));
    }) as ChatProviderMessage['message'][];

    console.debug('[AtlasAiBackend] Handle message:', message, { contextMessages });

    if (!hasRole(message, 'user')) {
      return;
    }
    console.debug('Handle user message', contextMessages);
    try {
      const runner = aiClient.chat.completions
        .runTools(
          {
            ...defaultPayload,
            stream: true,
            messages: contextMessages,
            // biome-ignore lint/style/useNamingConvention: API expects snake_case
            tool_choice: 'auto',
            tools: toolDefinitions.map((toolDefinition) =>
              produce(toolDefinition, (draft) => {
                draft.function.function = async (args: unknown, runner) => {
                  try {
                    const tool = toolsByName.get(toolDefinition.function.name ?? '');
                    if (!tool) {
                      throw new Error(`No definition found for tool: ${toolDefinition.function.name}`);
                    }

                    // console.debug('Invoking tool:', tool.name, args, JSON.stringify(runner.messages, null, 2));
                    const { message: toolResponseObject, abort } = await tool.handler({
                      id: tool.name,
                      args,
                      message,
                      messages: contextMessages,
                      aiClient,
                      setContext: (value) => {
                        console.info('TODO[user]: Update chat context from tool:', value);
                      },
                    });

                    if (abort) {
                      runner.abort();
                    }

                    return toolResponseObject;
                  } catch (e) {
                    throw new ToolInvocationError('Tool invocation failed', { cause: e });
                  }
                };
              }),
            ),
          },
          { maxRetries: 2 },
        )
        .on('message', (msg) => {
          if (msg.role !== 'assistant' && msg.role !== 'tool') {
            return;
          }
          addMessage({ message: msg });
        });

      setStreamingMessage(runner);
      const response = await runner.finalChatCompletion();
      setStreamingMessage(null);

      try {
        const [choice] = response.choices;
        if (!choice) {
          throw new Error(`No choice found in response: ${JSON.stringify(response)}`);
        }
        return choice;
      } catch (error) {
        console.error('Error while executing chat completion', error);
        throw error;
      }
    } catch (e) {
      console.error('Failed to get completion:', e);

      setStreamingMessage(null);
      if (e instanceof APIUserAbortError) {
        console.debug('Tool call was aborted by user or tool call');
        return;
      }

      const error =
        e instanceof Error
          ? {
              message: e.message,
              name: e.name,
              // stack: e.stack,
              cause: recursiveGetErrorCause(e.cause),
            }
          : {};

      addMessage({
        message: {
          role: 'assistant',
          content: ['**An error has occurred:**', error.message ?? e?.toString(), error.cause]
            .filter((it) => !!it)
            .join('\n\n'),
        },
        error,
      });
    }
  });

  return null;
}

AtlasAiBackend.Hero = AtlasAiHero;

const recursiveGetErrorCause = (error: unknown): string[] => {
  if (typeof error === 'string') {
    return [error];
  }
  if (error instanceof Error && error.cause) {
    return [error.message, ...recursiveGetErrorCause(error.cause)];
  }
  return [];
};
